import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState = {
};

interface SearchParams {
  createDateFrom: string,
  createDateTo: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  createDateFrom: dayjs().subtract(1, "month").format("YYYYMMDD"),
  createDateTo: dayjs().format("YYYYMMDD"),
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function SntrDiscrepancyStatus(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "입고구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "erpReqQty", width: 150, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 150, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/report/sntr_discrepancy_status.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/report/sntr_discrepancy_status", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/report/sntr_discrepancy_status", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/report/sntr_discrepancy_status", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/report/sntr_discrepancy_status.download?mode=search", searchParams, {
        "fileName": "SN TR불일치현황_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>SN/TR 불일치 현황</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-range">
            <p>조회일자</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.createDateFrom)}
                  onChange={(e) => {doSearch(e, {createDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.createDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.createDateTo)}
                  onChange={(e) => {doSearch(e, {createDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.createDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 불일치 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 불일치 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(SntrDiscrepancyStatus);
